/**
 * 월간 보고서 자동 발송 기록 (운영자 › 보고서 발송 › 자동 발송) — 261030-report-auto-dispatch.
 * 구독 기간 끝날 10시 이후 서버가 보고서를 고정하고 알림톡으로 링크를 보낸다. 여기서는 결과를 보고, 보낸 보고서를 열고, 다시 보낸다.
 *
 * ★ 실패는 **자동으로 다시 보내지 않는다** — 같은 사람에게 두 번 갈 수 있어서다. 「다시 보내기」가 유일한 길이고 새 링크를 발급한다
 *   (옛 링크는 죽는다).
 */
import { reportDispatch } from '@geo-admin/shared/contract/api/index.ts';
import type { ReportDispatchRow } from '@geo-admin/shared/contract/api/reportDispatch.ts';
import { useState } from 'react';
import { useBrandKind } from './BrandKindTabs.tsx';
import { ReportPage } from './ReportPageView.tsx';
import { Button } from './ui/button.tsx';
import { Badge, Card, CardBody } from './ui/card.tsx';
import { Dialog } from './ui/dialog.tsx';
import { ErrorBox, Loading } from './ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

const md = (iso: string) => `${Number(iso.slice(5, 7))}/${Number(iso.slice(8, 10))}`;

const STATUS: Record<
  ReportDispatchRow['status'],
  { label: string; tone: 'slate' | 'green' | 'amber' | 'red' | 'blue' }
> = {
  claimed: { label: '만드는 중', tone: 'blue' },
  ready: { label: '보내기 전', tone: 'amber' },
  sent: { label: '보냄', tone: 'green' },
  failed: { label: '실패', tone: 'red' },
  skipped: { label: '건너뜀', tone: 'slate' },
};

export function ReportDispatchAdmin() {
  const brandKind = useBrandKind();
  const q = useApi(reportDispatch.listReportDispatches, { query: { brandKind } });
  const [open, setOpen] = useState<string | null>(null);

  if (q.isLoading) return <Loading label="발송 기록을 불러오는 중" />;
  if (q.error) return <ErrorBox error={q.error} />;
  const items = q.data?.items ?? [];
  return (
    <>
      {q.data && !q.data.enabled ? (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-900 text-sm">
          자동 발송이 꺼져 있습니다(<code>--report-dispatch</code>). 카카오 템플릿 승인 뒤에 켭니다.
        </p>
      ) : null}
      <p className="text-slate-600 text-sm">
        구독 기간 끝날 10시 이후, 이용 중인 브랜드에 보고서 링크를 알림톡으로 보냅니다. 최근 60일{' '}
        <b>{items.length}건</b>
      </p>
      {items.length === 0 ? (
        <p className="py-8 text-center text-slate-500 text-sm">아직 보낸 보고서가 없습니다</p>
      ) : null}
      {items.map((d) => (
        <DispatchCard key={d.id} d={d} onOpen={() => setOpen(d.id)} />
      ))}
      <Dialog open={open !== null} onClose={() => setOpen(null)} title="보낸 보고서">
        {open ? <DispatchPreview id={open} /> : null}
      </Dialog>
    </>
  );
}

function DispatchCard({ d, onOpen }: { d: ReportDispatchRow; onOpen: () => void }) {
  const resend = useApiMutation(reportDispatch.resendReportDispatch);
  const [msg, setMsg] = useState<string | null>(null);
  const s = STATUS[d.status];
  return (
    <Card>
      <CardBody className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <b className="text-slate-900">{d.brandName}</b>
          <Badge tone={s.tone}>{s.label}</Badge>
          <Badge tone="slate">{d.kind === 'renewal' ? `갱신 ${md(d.nextOn)}` : `종료 ${md(d.nextOn)}`}</Badge>
          <span className="text-slate-500 text-xs">
            회차 {d.cycleFrom ? md(d.cycleFrom) : '?'}~{md(d.cycleTo)} · {dateTime(d.createdAt)}
          </span>
          <span className="ml-auto flex gap-2">
            {d.viewable ? (
              <Button size="sm" variant="outline" onClick={onOpen}>
                보고서 보기
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              disabled={resend.isPending}
              onClick={() => {
                if (
                  !window.confirm(
                    `${d.brandName}에 알림톡을 다시 보냅니다. 새 링크가 나가고 옛 링크는 열리지 않습니다.`,
                  )
                )
                  return;
                setMsg(null);
                resend.mutate(
                  { params: { id: d.id } },
                  {
                    onSuccess: (r) => setMsg(`${r.recorded}명 중 ${r.sent}명에게 보냈습니다`),
                    onError: (e) => setMsg(e.message),
                  },
                );
              }}
            >
              다시 보내기
            </Button>
          </span>
        </div>
        {d.lastError ? <p className="text-red-700 text-xs">{d.lastError}</p> : null}
        {msg ? <p className="text-slate-700 text-xs">{msg}</p> : null}
        {d.notices.length ? (
          <ul className="space-y-0.5 text-slate-600 text-xs">
            {d.notices.map((n) => (
              <li key={`${n.at}:${n.recipient}`}>
                {dateTime(n.at)} · {n.recipient ?? '번호 없음'} · {STATUS[n.status].label}
                {n.detail && n.status !== 'sent' ? ` — ${n.detail}` : ''}
              </li>
            ))}
          </ul>
        ) : null}
      </CardBody>
    </Card>
  );
}

function DispatchPreview({ id }: { id: string }) {
  const q = useApi(reportDispatch.viewReportDispatch, { params: { id } });
  if (q.isLoading) return <Loading label="불러오는 중" />;
  if (q.error) return <ErrorBox error={q.error} />;
  return q.data ? (
    <div className="-m-4 max-h-[75vh] overflow-y-auto">
      <ReportPage view={q.data} />
    </div>
  ) : null;
}
